import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  TutorQuizConfigDTO,
  getCourseQuizConfig,
  saveCourseQuizConfig,
} from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
}

const QUESTION_TYPE_OPTIONS = [
  { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True / False' },
];

function parseTypes(csv?: string): string[] {
  if (!csv) return ['MULTIPLE_CHOICE'];
  return csv.split(',').map((t) => t.trim()).filter(Boolean);
}

export default function TutorCourseQuizConfigPanel({ courseId }: Props) {
  const [config, setConfig] = useState<TutorQuizConfigDTO>({});
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['MULTIPLE_CHOICE']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    getCourseQuizConfig(courseId)
      .then((data) => {
        setConfig(data);
        setSelectedTypes(parseTypes(data.allowedQuestionTypes));
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load quiz config', severity: 'error' }))
      .finally(() => setLoading(false));
  }, [courseId]);

  const handleTypeToggle = (value: string) => {
    setSelectedTypes((prev) => {
      if (prev.includes(value)) {
        if (prev.length === 1) return prev; // keep at least one
        return prev.filter((t) => t !== value);
      }
      return [...prev, value];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveCourseQuizConfig(courseId, {
        ...config,
        allowedQuestionTypes: selectedTypes.join(','),
      });
      setConfig(saved);
      setSelectedTypes(parseTypes(saved.allowedQuestionTypes));
      setSnackbar({ open: true, message: 'Quiz config saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save quiz config', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold">Course Quiz Configuration</Typography>

      <TextField
        label="Max Questions"
        type="number"
        size="small"
        value={config.maxQuestions ?? ''}
        onChange={(e) => setConfig({ ...config, maxQuestions: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Time Limit (minutes)"
        type="number"
        size="small"
        value={config.timeLimitMinutes ?? ''}
        onChange={(e) => setConfig({ ...config, timeLimitMinutes: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Min Pass % "
        type="number"
        size="small"
        value={config.minPassPercent ?? ''}
        onChange={(e) => setConfig({ ...config, minPassPercent: e.target.value ? Number(e.target.value) : undefined })}
      />
      <TextField
        label="Max Retries"
        type="number"
        size="small"
        value={config.maxRetries ?? ''}
        onChange={(e) => setConfig({ ...config, maxRetries: e.target.value ? Number(e.target.value) : undefined })}
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!config.allowAiGeneration}
            onChange={(e) => setConfig({ ...config, allowAiGeneration: e.target.checked })}
          />
        }
        label="Allow AI Generation"
      />

      <Box>
        <FormLabel component="legend">Allowed Question Types (select at least one)</FormLabel>
        <FormGroup row>
          {QUESTION_TYPE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selectedTypes.includes(opt.value)}
                  onChange={() => handleTypeToggle(opt.value)}
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>
      </Box>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Config'}
      </Button>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
